import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

export type NavEntry = {
  key: string;
  path: string;
  label: string;
};

type NavHistoryContextValue = {
  entries: NavEntry[];
  setLabel: (key: string, label: string) => void;
};

const NavHistoryContext = createContext<NavHistoryContextValue | null>(null);

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function defaultLabelFor(pathname: string, routeLabels: Record<string, string>): string {
  const segs = pathname.split("/").filter(Boolean);
  const parts: string[] = [];
  for (const s of segs) {
    if (!s || UUID_RE.test(s)) continue;
    parts.push(routeLabels[s] ?? s);
  }
  if (parts.length === 0) return routeLabels[""] ?? "Home";
  return parts.join(" › ");
}

export function NavHistoryProvider({
  children,
  routeLabels,
  rootPaths,
}: {
  children: React.ReactNode;
  routeLabels: Record<string, string>;
  rootPaths: ReadonlySet<string>;
}) {
  const location = useLocation();
  const navType = useNavigationType();
  const [entries, setEntries] = useState<NavEntry[]>(() => [
    {
      key: location.key,
      path: location.pathname + location.search,
      label: defaultLabelFor(location.pathname, routeLabels),
    },
  ]);

  useEffect(() => {
    const key = location.key;
    const path = location.pathname + location.search;
    const label = defaultLabelFor(location.pathname, routeLabels);

    setEntries((prev) => {
      // Hard reset (e.g. initial load, direct URL entry)
      if (key === "default") {
        return [{ key, path, label }];
      }

      // POP — user pressed back/forward → trim stack to that entry
      const idx = prev.findIndex((e) => e.key === key);
      if (idx >= 0) {
        return prev.slice(0, idx + 1);
      }

      // Same path — no-op
      const last = prev[prev.length - 1];
      if (last && last.path === path) {
        return prev;
      }

      // REPLACE — swap the last entry in place
      if (navType === "REPLACE" && prev.length > 0) {
        const next = prev.slice(0, -1);
        next.push({ key, path, label });
        return next;
      }

      // PUSH — lateral vs. deeper navigation:
      // If destination is a root/sidebar path → reset (lateral sidebar switch)
      // Otherwise → append (drilling deeper, even across sections)
      if (rootPaths.has(location.pathname)) {
        return [{ key, path, label }];
      }

      // Destination path already on the stack — e.g. an in-app "back" button
      // that PUSHes a parent path, or re-opening the same record. Collapse to
      // that entry instead of stacking a duplicate crumb, and adopt the current
      // location.key so browser back/forward and breadcrumb jumps stay in sync.
      const existingIdx = prev.findIndex((e) => e.path === path);
      if (existingIdx >= 0) {
        const next = prev.slice(0, existingIdx + 1);
        next[existingIdx] = { key, path, label };
        return next;
      }

      return [...prev, { key, path, label }];
    });
  }, [location.key, location.pathname, location.search, routeLabels, navType, rootPaths]);

  const setLabel = useCallback((key: string, label: string) => {
    setEntries((prev) => {
      const idx = prev.findIndex((e) => e.key === key);
      if (idx < 0) return prev;
      const cur = prev[idx]!;
      if (cur.label === label) return prev;
      const next = prev.slice();
      next[idx] = { key: cur.key, path: cur.path, label };
      return next;
    });
  }, []);

  const value = useMemo(() => ({ entries, setLabel }), [entries, setLabel]);
  return (
    <NavHistoryContext.Provider value={value}>
      {children}
    </NavHistoryContext.Provider>
  );
}

export function useNavHistory(): NavHistoryContextValue {
  const ctx = useContext(NavHistoryContext);
  if (!ctx) throw new Error("useNavHistory must be used inside NavHistoryProvider");
  return ctx;
}

export function usePageTitle(label: string | null | undefined) {
  const { setLabel } = useNavHistory();
  const location = useLocation();
  useEffect(() => {
    if (!label) return;
    setLabel(location.key, label);
  }, [label, location.key, setLabel]);
}
